import { BranchQuoteDefaults, ResultGetBranchQuoteDefaults } from '../../../../api/dealer-api-interface-franchisee';
import { branchQuoteDefaultsApi } from '../../data/v6/branch-quote-defaults';
import { clone, compare, compareAnalyze } from '../../../../../webmodule-common/other/clone';
import { customElement, state } from 'lit/decorators.js';
import { emptyGuid, newGuid } from '../../../../../webmodule-common/other/api/guid';
import { fireQuickErrorToast, fireQuickSuccessToast } from '../../../../../webmodule-common/other/toast-away';
import { FormInputAssistantBase } from '../../../../../webmodule-common/other/ui/templateresult/form-input-assistant-base';
import { getApiFactory } from '../../../../api/api-injector';
import { getCurrentUser } from '../../../../../webmodule-common/other/api/current-user';
import { getSupplier } from '../../supplier-selector';
import { html } from 'lit';
import { v6Editors } from '../../../../../webmodule-common/interop/interfaces/v6-config';
import { isValidV6ConfigVersion } from '../../../../../webmodule-common/interop/interfaces/v6-config';
import { LangV6QuoteSupplierDefaultsView } from '../../../../../localization/scripts/quotes/v6/v6-quote-supplier-defaults-view-lang';
import { LitElementBase } from '../../../../../webmodule-common/other/litelement-base';
import { lockUIandExecute } from '../../../../../webmodule-common/other/ui-lock';
import {
  QuoteDefaultsDataProvider,
  QuoteFrameItemMarker,
  ResultQuoteFrameItemUpdated,
  Snippet,
  V6QuoteDefaultsEditor
} from '../../../../../webmodule-common/interop/webmodule-interop';
import { userDataStore } from '../../../common/current-user-data-store';
@customElement('wm-franchiseev6branchquotesupplierdefaultsview')
export class FranchiseeV6BranchQuoteSupplierDefaultsView extends LitElementBase implements QuoteDefaultsDataProvider {
  supplierId: string;
  defaultOptions: BranchQuoteDefaults | null = null;
  options?: unknown | null;
  backup: unknown;
  userId: string = getCurrentUser()?.id ?? emptyGuid;

  private applyAndBackupDefaults(defaults: ResultGetBranchQuoteDefaults | null) {
    //search on user id, even though not implemented yet. for future proofing.
    const primary =
      defaults?.branchQuoteDefaultsPrimary.find(x => x.systemUserId === this.userId) ??
      defaults?.branchQuoteDefaultsPrimary.find(x => x.systemUserId === emptyGuid);

    this.defaultOptions = primary
      ? (defaults?.branchQuoteDefaults.find(x => x.id === primary.branchQuoteDefaultsId) ?? null)
      : null;
    this.options = this.defaultOptions?.data ? JSON.parse(this.defaultOptions?.data) : null;
    if (Array.isArray(this.options) && this.options.length === 0) {
      this.options = null;
    }
    this.backup = clone(this.options);
  }
  private async loadDefaults(): Promise<void> {
    //load all including userid, even though this is not implemented yet.
    const defaults = await branchQuoteDefaultsApi.run({
      branchId: this.supplierReferenceOverrideKey,
      supplierId: this.supplierId,
      systemUserId: this.userId
    });
    this.applyAndBackupDefaults(defaults);
  }

  protected getQuoteDefaultOptions(): unknown | null {
    return this.options;
  }

  protected setQuoteDefaultOptions(options: unknown) {
    this.options = options;
  }

  protected getQuoteIGUs(): unknown /* V6QuoteIGU[] */ {
    return [];
  }
  async getBaseLineQuoteDefaults(): Promise<unknown | undefined> {
    await this.loadDefaults();
    return this.options;
  }
  @state()
  v6editor: V6QuoteDefaultsEditor | null;

  constructor() {
    super();
  }
  public async discardChanges(): Promise<void> {
    this.options = clone(this.backup);
    await this.v6editor?.refresh?.();
  }

  public async saveChanges(): Promise<boolean> {
    if (!compare(this.options, this.backup)) {
      if (globalThis?.isDevelopmentMode) {
        console.debug('Quote Defaults Changes Found');
        console.debug(compareAnalyze(this.options, this.backup));
      }
      //save with empty userid for now, as user specific defaults are not implemented yet.
      const result = await getApiFactory()
        .franchisee()
        .updateBranchQuoteDefaults({
          branchQuoteDefaults: {
            id: this.defaultOptions?.id ?? newGuid(),

            branchId: this.defaultOptions?.branchId ?? this.supplierReferenceOverrideKey,
            templateName: '',
            systemUserId: emptyGuid,
            supplierId: this.supplierId,
            data: JSON.stringify(this.options ?? [])
          },
          isPrimary: true
        });
      if (result) {
        fireQuickSuccessToast(LangV6QuoteSupplierDefaultsView.saveChangesSuccess());
      } else {
        fireQuickErrorToast(LangV6QuoteSupplierDefaultsView.saveChangesError());
      }
      if (!result) return false;
    }
    return true;
  }
  public async refresh(): Promise<void> {
    await this.v6editor?.refresh?.();
  }

  async saveItem(_frameData: ResultQuoteFrameItemUpdated) {
    return true;
  }
  get supplierReferenceOverrideKey(): string {
    return userDataStore.defaultBranch.id;
  }

  get v6UI(): HTMLElement | null {
    return this.v6editor?.ui ?? null;
  }

  get readonly(): boolean {
    return this.v6UI === null;
  }

  get quoteIGUs(): unknown {
    return [];
  }

  async updateQuoteItemsBegin(): Promise<boolean> {
    return true;
  }

  applyQuoteDefaultsHeaderTemplate() {
    return undefined;
  }

  async updateQuoteItemsEnd(_updatedCount: number): Promise<void> {
    //do nothign
  }

  async dispose(): Promise<void> {
    //add nullable to deal with version missing info
    if (this.v6UI) this.v6UI.remove();

    await this.v6editor?.releaseResources?.();
    this.v6editor = null;

    if (!this.v6editor) console.warn('V6QuoteSupplierDefaultsView: No V6 Editor available');
  }

  async quoteItems(): Promise<QuoteFrameItemMarker[]> {
    return [];
  }

  getQuoteDefaults(): unknown {
    return this.getQuoteDefaultOptions();
  }

  saveQuoteDefaults(quoteDefaultsData: unknown): boolean {
    this.setQuoteDefaultOptions(quoteDefaultsData);
    return true;
  }

  public async afterConstruction(): Promise<void> {
    this.supplierId = (await getSupplier()).supplierId;
    await this.loadDefaults();
    this.v6editor = isValidV6ConfigVersion() ? v6Editors().getQuoteDefaultsEditorService(this) : null;
    await this.v6editor?.refresh?.();
    if (!this.v6editor) console.warn('V6QuoteSupplierDefaultsView: No V6 Editor available');
    if (!this.backup) {
      this.v6editor?.prepareForSave();
      this.backup = clone(this.options);
    }
  }

  buttonMenu(): Snippet {
    const form = new FormInputAssistantBase();
    // Reset button event
    const resetEvent = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      await lockUIandExecute(async () => {
        await this.v6editor?.resetQuoteDefaults();
        await this.saveChanges();
      });
    };

    // Apply button event
    const applyEvent = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      await lockUIandExecute(async () => {
        await this.saveChanges();
      });
    };

    const resetDefaultsButton = form.buttonDefault(LangV6QuoteSupplierDefaultsView.resetDefaults(), {
      size: 'small',
      events: { click: resetEvent },
      readonly: !this.canModify() || this.readonly
    });
    const applyButton = form.buttonDefault(LangV6QuoteSupplierDefaultsView.save(), {
      size: 'small',
      events: { click: applyEvent },
      readonly: !this.canModify() || this.readonly
    });
    return this.v6UI !== null ? html`${resetDefaultsButton} ${applyButton}` : html``;
  }

  protected canModify() {
    return true;
  }

  protected render() {
    const ui = this.v6UI;
    const header = html`<h1>${LangV6QuoteSupplierDefaultsView.branchQuoteDefaultsTitle()}</h1>
      <h2>${LangV6QuoteSupplierDefaultsView.branchQuoteDefaultsSubtitle()}</h2>`;
    return ui ? html`${header}${ui}` : html`${LangV6QuoteSupplierDefaultsView.supplierServiceNotAvailable()}`;
  }
}
